import { TrackingEventConstants } from '../../../../../constants/tracking'
import { useEffect, useCallback, useState } from 'react'
import PropTypes from 'prop-types'
import { useSelector, useDispatch } from 'react-redux'
import classnames from 'classnames'
import { FormattedMessage } from 'react-intl'
import { formatAbbrCurrency } from '../../../../../helpers/formatters'
import {
  Avatar,
  Button,
  Email,
  Form,
  FormField,
  List,
  If,
  Unless,
  NMLS,
  Phone,
  Ruler,
  Text,
  StatusIcon
} from '../../../..'
import { IconCheck, IconArrowLeft, IconArrowRight } from '../../../../shared/Icon'
import DollarInput from '../../../../shared/Input/DollarInput'
import { useTuneValue } from '../../../../../hooks/useTuneValue'
import { fetchAverageMedianPrice } from '../../../../../actions/buyerOnboarding'
import { KEY_CODES } from '../../../../../constants/utility'
import {
  selectFullName,
  selectCustomerFirstName,
  selectCustomerPhotoUri,
  selectCustomerPhone,
  selectCustomerEmail,
  selectCustomerNMLS
} from '../../../../../store/selectors/customerProfile'
import { selectAffordabilityIsDefault, selectAffordabilityIsPrequal } from '../../../../../store/selectors/buyerInfo'
import {
  selectShowHomeEquityRelatedPrice,
  selectAverageMedianPrice,
  selectHasCitysAvgMedianPrice,
  selectBuyerOnboardingPricePoint,
  selectBuyerOnboardingFinalStepLoading
} from '../../../../../store/selectors/buyerOnboarding'
import { selectAnswerCityName, selectAnswerCityCenter } from '../../../../../store/selectors/client'
import withComposedClassName from '../../../../../composables/withComposedClassName'

import baseStyles from '../../BuyerOnboardingWizard.module.scss'
import styles from './BuyerOnboarded.module.scss'
import { useTrackingContext } from '../../../../../providers/tracking'

const DOLLARS = 'dollars'
const useStep = 10000
export const roundInputValue = price => Math.round(price / useStep) * useStep
const StyledDollarInput = withComposedClassName(styles.input)(DollarInput)

const BuyerOnboarded = ({ handleSubmitClick, setWizardTuneValue }) => {
  const dispatch = useDispatch()
  const { trackingClient } = useTrackingContext()
  const [interacted, setInteracted] = useState(false)
  const name = useSelector(selectFullName)
  const firstName = useSelector(selectCustomerFirstName)
  const photoUri = useSelector(selectCustomerPhotoUri)
  const phone = useSelector(selectCustomerPhone)
  const email = useSelector(selectCustomerEmail)
  const nmls = useSelector(selectCustomerNMLS)
  const averageMedianPrice = useSelector(selectAverageMedianPrice)
  const answerCity = useSelector(selectAnswerCityName)
  const latLng = useSelector(selectAnswerCityCenter)
  const hasCitysAvgMedianPrice = useSelector(selectHasCitysAvgMedianPrice)
  const loading = useSelector(selectBuyerOnboardingFinalStepLoading)
  const pricePoint = useSelector(selectBuyerOnboardingPricePoint)
  const showHomeEquityRelatedPrice = useSelector(selectShowHomeEquityRelatedPrice)
  const hasAffordabilitySet = !useSelector(selectAffordabilityIsDefault)
  const affordabilityIsPrequal = useSelector(selectAffordabilityIsPrequal)

  const bounds = {
    homebotAmount: pricePoint,
    customerAmount: null,
    low: 0,
    high: 3000000
  }

  const roundInputValue = price => Math.round(price / useStep) * useStep

  const {
    tuneValue,
    rulerValue,
    inputValue,
    inputFocused,
    setTuneValue,
    setRulerValue,
    setInputValue,
    setInputFocused,
    useMin,
    useMax,
    usePins,
    getValueFromEvent
  } = useTuneValue({
    price: null,
    useStep,
    bounds
  })

  const onSubmitClick = () => {
    trackingClient?.clientCustomized({
      customization_type: TrackingEventConstants.CLIENT_UPDATED_PRICE_POINT
    })
    trackingClient?.buyerOnboarded({ price_point_set: tuneValue })
    handleSubmitClick()
  }

  const updateTuneValues = useCallback(
    price => {
      setInputValue(roundInputValue(price))
      setTuneValue(roundInputValue(price))
    },
    [setInputValue, setTuneValue]
  )

  useEffect(() => {
    setWizardTuneValue(tuneValue)
  }, [tuneValue, setWizardTuneValue])

  useEffect(() => {
    if (!!latLng && !averageMedianPrice) {
      dispatch(fetchAverageMedianPrice(latLng))
    }
  }, [dispatch, latLng, averageMedianPrice])

  useEffect(() => {
    if (!loading) {
      updateTuneValues(pricePoint)
    }
  }, [pricePoint, updateTuneValues, loading])

  return (
    <>
      <If condition={loading}>
        <StatusIcon spin />
      </If>
      <Unless condition={loading}>
        <div data-qa='confirmation' className={baseStyles.step}>
          <div>
            <Text.Headline>
              <FormattedMessage
                id='NewBuyerOnboarding.buyerOnboarded.headline'
                defaultMessage="What's your ideal budget?"
              />
            </Text.Headline>
            <If condition={hasAffordabilitySet}>
              <If condition={affordabilityIsPrequal}>
                <Text size='s' variant='neutralCool' variantCount='600' align='center' className={styles.subheader}>
                  <FormattedMessage
                    data-testid='home-equity-subheadline'
                    id='NewBuyerOnboarding.buyerOnboarded.affordability.subheadline.prequal'
                    defaultMessage="Based off the prequalification that {firstName} created for you, we've set your budget to:"
                    values={{ firstName }}
                  />
                </Text>
              </If>
              <Unless condition={affordabilityIsPrequal}>
                <Text size='s' variant='neutralCool' variantCount='600' align='center' className={styles.subheader}>
                  <FormattedMessage
                    data-testid='home-equity-subheadline'
                    id='NewBuyerOnboarding.buyerOnboarded.affordability.subheadline.pricepoint'
                    defaultMessage='{firstName} has set your initial budget to:'
                    values={{ firstName }}
                  />
                </Text>
              </Unless>
            </If>
            <Unless condition={hasAffordabilitySet}>
              <If condition={showHomeEquityRelatedPrice}>
                <Text size='s' variant='neutralCool' variantCount='600' align='center' className={styles.subheader}>
                  <FormattedMessage
                    data-testid='home-equity-subheadline'
                    id='NewBuyerOnboarding.buyerOnboarded.subheadline'
                    defaultMessage="Based on Homebot's quick analysis of your current home equity, we've set your budget to:"
                  />
                </Text>
              </If>
              <If condition={hasCitysAvgMedianPrice && !showHomeEquityRelatedPrice}>
                <Text size='s' variant='neutralCool' variantCount='600' align='center' className={styles.subheader}>
                  <FormattedMessage
                    data-testid='average-median-subheadline'
                    id='NewBuyerOnboarding.buyerOnboarded.subheadlineWithCity'
                    values={{ answerCity }}
                    defaultMessage="Based on Homebot's quick analysis of {answerCity}'s average home price, we've set your budget to:"
                  />
                </Text>
              </If>
              <If condition={!hasCitysAvgMedianPrice && !showHomeEquityRelatedPrice}>
                <Text size='s' variant='neutralCool' variantCount='600' align='center' className={styles.subheader}>
                  <FormattedMessage
                    data-testid='national-median-subheadline'
                    id='NewBuyerOnboarding.buyerOnboarded.subheadlineNational'
                    defaultMessage="Based on the national median home price, we've set your budget to:"
                  />
                </Text>
              </If>
            </Unless>
          </div>
          <Form fields={[{ name: DOLLARS, parse: DollarInput.parseValue }]}>
            <div className={styles.rulerWrapEscape}>
              <div className={styles.rulerWrap}>
                <div className={styles.valueDisplay}>
                  <FormField
                    input={StyledDollarInput}
                    hideLabel
                    value={inputValue || tuneValue}
                    data-qa='input-tune-value'
                    name={DOLLARS}
                    onMouseOver={() => setInteracted(true)}
                    onTouch={() => setInteracted(true)}
                    onFocus={() => setInputFocused(true)}
                    onBlur={e => {
                      const value = getValueFromEvent(e)
                      if (value && value !== inputValue) {
                        const rounded = Math.round(value / useStep) * useStep
                        if (rulerValue !== rounded) {
                          setRulerValue(null)
                        }
                        setInputValue(null)
                        setTuneValue(`${value}`)
                      }
                      setInputFocused(false)
                    }}
                    onKeyDown={e => {
                      e.stopPropagation()
                      if ((e.which || e.keyCode) === KEY_CODES.enter) {
                        e.target.blur()
                      }
                    }}
                  />
                  <Button
                    data-qa='confirmation-value-btn'
                    tabIndex='-1'
                    type='button'
                    className={classnames(styles.confirm, inputFocused && styles.showConfirm)}
                    iconStart={IconCheck}
                  />
                </div>
                {/* eslint-disable-next-line jsx-a11y/mouse-events-have-key-events */}
                <div
                  className={styles.rulerEscape}
                  onTouch={() => setInteracted(true)}
                  onMouseOver={() => setInteracted(true)}
                >
                  <Ruler
                    className={styles.ruler}
                    value={rulerValue || tuneValue}
                    min={useMin}
                    max={useMax}
                    step={useStep}
                    pins={usePins}
                    minorTick={useStep * 5}
                    majorTick={useStep * 10}
                    tickColor='green'
                    intervalWidth={8}
                    labelRenderFunc={formatAbbrCurrency}
                    onChange={e => {
                      const value = getValueFromEvent(e)
                      if (value) {
                        setRulerValue(value)
                        if (inputValue !== null) {
                          setInputValue(value)
                          setTuneValue(value)
                        }
                      }
                    }}
                  />
                </div>
              </div>
            </div>
          </Form>
          <div className={styles.legend}>
            <Text
              variant='neutralCool'
              variantCount='600'
              size='xs'
              className={classnames(styles.arrowDirections, interacted && styles.hide)}
            >
              <IconArrowLeft />
              <FormattedMessage
                id='NewBuyerOnboarding.buyerOnboarded.arrow.direction'
                defaultMessage='Adjust the slider to dial in your budget'
              />
              <IconArrowRight />
            </Text>
          </div>
          <Button className={styles.button} onClick={onSubmitClick} data-qa='confirmation-btn'>
            <FormattedMessage id='NewBuyerOnboarding.buyerOnboarded.lets.go.button' defaultMessage="Let's go!" />
          </Button>
          <div className={styles.footer}>
            <Avatar className={styles.avatar} src={photoUri} />
            <Text variant='neutralCool' variantCount='600' size='s'>
              <FormattedMessage
                id='BuyerOnboarding.startExploring.footer'
                defaultMessage='Along the way, {name} is here to answer any questions you have!'
                values={{ name }}
              />
            </Text>
            <List.Bulleted.Inline>
              {phone && <Phone value={phone} />}
              {email && <Email value={email} text='Email' />}
              {nmls && <NMLS value={nmls} variant='neutralCool' variantCount='600' />}
            </List.Bulleted.Inline>
          </div>
        </div>
      </Unless>
    </>
  )
}

BuyerOnboarded.propTypes = {
  handleSubmitClick: PropTypes.func,
  setWizardTuneValue: PropTypes.func
}

export default BuyerOnboarded
